import { DataType } from '../data';
import { BaseContext } from './base-context';
import { ThemeContext } from './theme-context';
import type { INotesMasterDataModel } from '../model/notes-master-data-model';
import type { PageDataType } from '../data';
import type { IBaseContext } from './base-context';
import type { INoteSlideDataModel } from '../model/note-slide-data-model';
import type { ICTColorMapping, ICTPlaceholderPml } from '@tencent/slide-sdk-pb-model';
import type { DeepReadonly } from 'ts-essentials';
import type { IThemeContext } from './theme-context';
import type { ILayoutDataModel } from '../model/layout-data-model';
import type { IMasterDataModel } from '../model/master-data-model';
import type { ISlideDataModel } from '../model/slide-data-model';
import type { IThemeDataModel } from '../model/theme-data-model';
import type { IShapeDataModel } from '../model/shape-data-model';

export interface IPageContext extends IBaseContext {
    getPageType(): PageDataType;
    getPage<
        Page extends
            | ISlideDataModel
            | ILayoutDataModel
            | IMasterDataModel
            | INoteSlideDataModel
            | INotesMasterDataModel,
    >(): Page;
    getSlide(): ISlideDataModel | undefined;
    getLayout(): ILayoutDataModel | undefined;
    getMaster(): IMasterDataModel | undefined;
    getTheme(): IThemeDataModel | undefined;
    getPlaceholder(ph?: DeepReadonly<ICTPlaceholderPml>): IShapeDataModel | undefined;
    getThemeContext(): IThemeContext;
}

export class PageContext extends BaseContext implements IPageContext {
    public static fromPageContext(pageContext: Required<IPageContext>) {
        const context = new PageContext(pageContext.getPageType());
        context.initContextFromPageContext(pageContext);
        return context;
    }

    private layout?: ILayoutDataModel;
    private master?: IMasterDataModel;
    private theme?: IThemeDataModel;
    private page?: ISlideDataModel | ILayoutDataModel | IMasterDataModel | INoteSlideDataModel | INotesMasterDataModel;
    private slide?: ISlideDataModel;
    private themeContext?: IThemeContext;

    public constructor(public pageType: PageDataType) {
        super();
    }

    public getPageType(): PageDataType {
        return this.pageType;
    }

    public getMaster(): IMasterDataModel | undefined {
        return this.master;
    }

    public getLayout(): ILayoutDataModel | undefined {
        return this.layout;
    }

    public setLayout(layout?: ILayoutDataModel): void {
        this.layout = layout;
    }

    public setMaster(master?: IMasterDataModel): void {
        this.master = master;
    }

    public getPlaceholder(ph?: DeepReadonly<ICTPlaceholderPml>): IShapeDataModel | undefined {
        if (!ph) {
            return;
        }

        if (this.pageType === DataType.Slide) {
            return this.getLayout()?.getPlaceholderShape(ph) ?? this.getMaster()?.getPlaceholderShape(ph);
        }

        if (this.pageType === DataType.SlideLayout) {
            return this.getMaster()?.getPlaceholderShape(ph);
        }
    }

    public getPage<
        Page extends
            | ISlideDataModel
            | ILayoutDataModel
            | IMasterDataModel
            | INoteSlideDataModel
            | INotesMasterDataModel,
    >(): Page {
        if (!this.page) {
            throw new Error('getPage from PageContext without setPage');
        }
        return this.page as Page;
    }

    public setPage(
        page: ISlideDataModel | ILayoutDataModel | IMasterDataModel | INoteSlideDataModel | INotesMasterDataModel,
    ): void {
        this.page = page;
    }

    public setSlide(slide: ISlideDataModel): void {
        this.slide = slide;
    }

    public getSlide(): ISlideDataModel | undefined {
        return this.slide;
    }

    public getTheme(): IThemeDataModel | undefined {
        return this.theme;
    }

    public setTheme(theme: IThemeDataModel): void {
        this.theme = theme;
        if (this.getAliasColorMap()) {
            this.themeContext = new ThemeContext({
                theme,
                aliasColorMap: this.getAliasColorMap(),
            });
        }
    }

    public getThemeContext(): IThemeContext {
        if (!this.themeContext) {
            this.themeContext = new ThemeContext({
                theme: this.getTheme(),
                aliasColorMap: this.getAliasColorMap(),
            });
        }
        return this.themeContext;
    }

    protected initContextFromPageContext(pageContext: IPageContext) {
        const theme = pageContext.getTheme();
        const slide = pageContext.getSlide();
        const target = pageContext.getTarget();

        this.setPresentation(pageContext.getPresentation());
        this.setMaster(pageContext.getMaster());
        this.setLayout(pageContext.getLayout());
        this.setPage(pageContext.getPage());
        if (target) {
            this.setTarget(target);
        }
        if (slide) {
            this.setSlide(slide);
        }
        if (theme) {
            this.setTheme(theme);
        }
    }

    private getAliasColorMap(): DeepReadonly<ICTColorMapping> | undefined {
        const master = this.getMaster();
        if (!master?.props) return;

        const masterColorMapping = master.props.clrMap;
        if ([DataType.Slide, DataType.SlideLayout].includes(this.pageType)) {
            const slide = this.getSlide();
            const layout = this.getLayout();
            const { overrideClrMapping: slideOvrClrMapping } = slide?.props?.clrMapOvr ?? { masterClrMapping: true };
            const { overrideClrMapping: layoutOvrClrMapping } = layout?.props?.clrMapOvr ?? { masterClrMapping: true };
            return slideOvrClrMapping ?? layoutOvrClrMapping ?? masterColorMapping;
        }

        return masterColorMapping;
    }
}
